import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from '../config/axiosConfig';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const eventSourceRef = useRef(null);
    const backendURL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

    // Initial fetch of notifications
    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/notifications/unread');
            const allNotifications = response.data.notifications || [];
            setNotifications(allNotifications);

            // Calculate unread count
            const unread = allNotifications.filter(n => !n.isRead).length;
            setUnreadCount(unread);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllNotifications = async () => {
        try {
            const response = await axios.get('/api/notifications');
            const allNotifications = response.data.notifications || [];
            setNotifications(allNotifications);

            // Recalculate unread count
            const unread = allNotifications.filter(n => !n.isRead).length;
            setUnreadCount(unread);
        } catch (error) {
            console.error('Error fetching all notifications:', error);
        }
    };

    // Handle new notification from SSE
    const handleNewNotification = (notification) => {

        setNotifications(prev => {
            // Check if notification already exists
            if (!prev.some(n => n._id === notification._id)) {
                // Add to beginning of array
                return [notification, ...prev];
            }
            return prev;
        });

        // Update unread count if notification is unread
        if (!notification.isRead) {
            setUnreadCount(count => count + 1);
        }
    };

    // Mark notification as read
    const markAsRead = async (notificationId) => {
        try {
            await axios.patch(`/api/notifications/${notificationId}/read`);

            // Update local state
            setNotifications(prev => prev.map(n =>
                n._id === notificationId ? { ...n, isRead: true } : n
            ));

            // Update unread count
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    // Mark all notifications as read
    const markAllAsRead = async () => {
        try {
            await axios.post('/api/notifications/read-all');

            // Update all notifications to read
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));

            // Reset unread count
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    };

    // Set up SSE connection
    useEffect(() => {
        // Fetch initial notifications
        fetchNotifications();

        // Set up SSE connection
        // console.log('Establishing SSE connection for notifications');
        eventSourceRef.current = new EventSource(`${backendURL}/api/notifications/stream`, {
            withCredentials: true
        });

        // Connection opened
        eventSourceRef.current.onopen = () => {
            // console.log('SSE connection established');
        };

        // Message received
        eventSourceRef.current.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                // Skip heartbeat messages
                if (data.type === 'heartbeat' || data.type === 'connected') {
                    return;
                }

                // Process notification
                handleNewNotification(data);
            } catch (error) {
                console.error('Error parsing SSE message:', error);
            }
        };

        // Error handling
        eventSourceRef.current.onerror = (error) => {

            // Close the connection
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }

            // Try to reconnect after delay
            setTimeout(() => {
                // console.log('Attempting to reconnect SSE');
                // The effect cleanup and re-run will handle reconnection
            }, 5000);
        };

        // Cleanup on unmount
        return () => {
            if (eventSourceRef.current) {
                // console.log('Closing SSE connection');
                eventSourceRef.current.close();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array - only run once on mount

    const value = {
        notifications,
        unreadCount,
        loading,
        markAsRead,
        markAllAsRead,
        setNotifications,
        setUnreadCount,
        fetchNotifications,
        fetchAllNotifications
      };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

// Custom hook to use the notification context
export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within NotificationProvider');
    }
    return context;
};